import { useId, useState, type FormEvent } from "react";
import {
  ANSWERED_OUTCOMES,
  MANUAL_CALL_INVALID_REASONS,
  MANUAL_CALL_MAX_MINUTES,
  manualCallResponseSchema,
  type LeadCardResponse,
  type ManualCallBody,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { callbackInstantOf } from "@gioia365/domain/callbacks";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import "../../design/components/select.css";
import "../../design/components/text-field.css";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericTime, localDayOf } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import type { Access } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { newRequestId } from "../../telephony/call-session-state";
import { refreshAfterCall } from "../../telephony/telephony-queries";
import { formatPhoneForDisplay } from "../lead-display";
import { phoneLabel } from "./CallButton";
import "./call-desk.css";
import "./callback.css";

// «Registra attività» (piano M5, Task 19, D15; mock-up `scheda-lead.html`): la telefonata fatta fuori dal CRM, dal cellulare o da un
// altro telefono, registrata dalla testata della scheda. Una finestra con il giorno e l'ora (di partenza adesso, nel fuso
// dell'azienda), l'esito in due passi come nella postazione («Risposta», «Nessuna risposta», «Numero errato» e, dopo «Risposta»,
// «Com'è andata?»), i minuti di conversazione (soltanto con una risposta, come nel mock-up), il numero chiamato (i telefoni del Lead
// nell'ordine della scheda) e gli appunti. Le regole dell'esito sono quelle della postazione, con le sue parole; l'ora non può essere
// nel futuro né prima che il Lead ci fosse. «Registra» manda tutto con una chiave per apertura (`requestId`: un nuovo invio dopo un
// guasto è la stessa telefonata), poi la scheda, la Timeline, gli elenchi e l'agenda dei Task si rileggono come dopo una chiamata
// (`refreshAfterCall`: la telefonata conta nei numeri, nella sequenza e può aver completato il richiamo). Un rifiuto del server dice
// il motivo, uno per codice.

type CallOutcome = ManualCallBody["outcome"];
type AnsweredOutcome = (typeof ANSWERED_OUTCOMES)[number];

/** Il primo passo dell'esito, nell'ordine della postazione (product owner, 25 settembre 2026). */
const FIRST_STEP: ReadonlyArray<{ value: CallOutcome; label: MessageKey }> = [
  { value: "answered", label: "callDesk.answered" },
  { value: "no_answer", label: "callDesk.noAnswer" },
  { value: "wrong_number", label: "callDesk.wrongNumber" },
];

/**
 * Chi guarda può registrare una telefonata su questo Lead: con «Chiamare e usare la postazione» e la modifica del Lead, da
 * un'appartenenza dell'azienda (mai il contesto di Gioia365). Nella «Guarda come…» sì, con chi guarda come autore. Il server decide
 * comunque.
 */
export function canRegisterCall(access: Access, card: LeadCardResponse): boolean {
  return !access.isPlatform && access.can("call") && card.permissions.canUpdate;
}

/** L'inizio del minuto: l'ora si sceglie al minuto, e il minuto in cui il Lead è nato vale già (come sul server). */
const minuteOf = (iso: string) => Math.floor(Date.parse(iso) / 60_000) * 60_000;

/** Il motivo di un rifiuto del server, dal codice. */
function refusalText(t: Translate, error: unknown): string {
  if (error instanceof ApiRequestError && error.code === "manual_call_invalid") {
    const reason = MANUAL_CALL_INVALID_REASONS.find((code) => code === error.details?.reason);
    if (reason) return t(`manualCall.invalid.${reason}`);
  }
  return messageForError(t, error);
}

/** Un telefono del Lead nella scelta del numero chiamato: il posto e il numero, «Cellulare · +39 300 000 0142». */
function phoneOptionText(t: Translate, card: LeadCardResponse, phone: LeadCardResponse["lead"]["phones"][number]): string {
  return `${phoneLabel(t, card, { holder: phone.holder, kind: phone.kind })} · ${formatPhoneForDisplay(phone.e164)}`;
}

type Props = { tab: TabContext; card: LeadCardResponse; timeZone: string; onClose: () => void };

export function ManualCallDialog({ tab, card, timeZone, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const formId = `${ids}-form`;
  const { lead } = card;
  // Una chiave per apertura: lo stesso invio ripetuto dopo un guasto è la stessa telefonata.
  const [requestId] = useState(newRequestId);
  const [opened] = useState(() => new Date().toISOString());
  const [day, setDay] = useState(() => localDayOf(timeZone, opened));
  const [time, setTime] = useState(() => formatNumericTime(timeZone, opened));
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [answeredOutcome, setAnsweredOutcome] = useState<AnsweredOutcome | null>(null);
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [phoneId, setPhoneId] = useState(lead.phones[0]?.id ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Il corpo da mandare, oppure il motivo per cui non si manda ancora. */
  function bodyOf(): { body: ManualCallBody } | { problem: string } {
    const at = callbackInstantOf(day, time, timeZone);
    if (!at) return { problem: t("errors.manual_call_invalid") };
    if (at.getTime() > Date.now()) return { problem: t("manualCall.invalid.future") };
    if (at.getTime() < minuteOf(lead.createdAt)) return { problem: t("manualCall.invalid.before_lead") };
    if (outcome === null) return { problem: t("callDesk.reasonNoOutcome") };
    if (outcome === "answered" && answeredOutcome === null) return { problem: t("callDesk.reasonNoAnswered") };
    const text = note.trim();
    if (outcome === "answered" && answeredOutcome === "other" && text === "") return { problem: t("callDesk.reasonNoNote") };
    const talked = Number(minutes);
    if (outcome === "answered" && (minutes.trim() === "" || !Number.isInteger(talked) || talked < 0 || talked > MANUAL_CALL_MAX_MINUTES)) {
      return { problem: t("manualCall.minutesInvalid") };
    }
    const phone = lead.phones.find((candidate) => candidate.id === phoneId);
    return {
      body: {
        at: at.toISOString(),
        outcome,
        answeredOutcome: outcome === "answered" ? answeredOutcome : null,
        minutes: outcome === "answered" ? talked : 0,
        note: text === "" ? null : text,
        phone: phone ? { holder: phone.holder, kind: phone.kind } : null,
        requestId,
      },
    };
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    const ready = bodyOf();
    if ("problem" in ready) {
      setError(ready.problem);
      return;
    }
    setError(null);
    setPending(true);
    try {
      await apiRequest("POST", `/api/leads/${encodeURIComponent(lead.id)}/calls/manual`, { schema: manualCallResponseSchema, body: ready.body, tab });
      refreshAfterCall(queryClient, tab, lead.id);
      toast.show(t("manualCall.saved"), "success");
      onClose();
    } catch (failure) {
      setPending(false);
      setError(refusalText(t, failure));
    }
  }

  const choose = (change: () => void) => {
    setError(null);
    change();
  };

  return (
    <Dialog
      open
      size="wide"
      title={t("manualCall.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={pending || day === "" || time === ""} aria-busy={pending ? "true" : undefined}>
            {t("manualCall.save")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void submit(event)} aria-busy={pending} noValidate>
        <p className="g-field__hint">{t("manualCall.intro")}</p>
        <div className="g-callback__row">
          <div className="g-field">
            <label className="g-field__label" htmlFor={`${ids}-day`}>
              {t("manualCall.day")}
            </label>
            <input
              id={`${ids}-day`}
              className="g-field__input"
              type="date"
              min={localDayOf(timeZone, lead.createdAt)}
              max={localDayOf(timeZone, new Date().toISOString())}
              value={day}
              onChange={(event) => choose(() => setDay(event.target.value))}
            />
          </div>
          <div className="g-field">
            <label className="g-field__label" htmlFor={`${ids}-time`}>
              {t("manualCall.time")}
            </label>
            <input id={`${ids}-time`} className="g-field__input" type="time" value={time} onChange={(event) => choose(() => setTime(event.target.value))} />
          </div>
        </div>
        <fieldset className="g-desk__fieldset">
          <legend>{t("callDesk.outcomeLegend")}</legend>
          <div className="g-desk__choices">
            {FIRST_STEP.map((option) => (
              <label key={option.value} className="g-desk__choice">
                <input
                  type="radio"
                  name={`${ids}-outcome`}
                  value={option.value}
                  checked={outcome === option.value}
                  onChange={() => choose(() => setOutcome(option.value))}
                />
                <span>{t(option.label)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {outcome === "answered" ? (
          <>
            <fieldset className="g-desk__fieldset">
              <legend>{t("callDesk.howDidItGo")}</legend>
              <div className="g-desk__choices">
                {ANSWERED_OUTCOMES.map((value) => (
                  <label key={value} className="g-desk__choice">
                    <input type="radio" name={`${ids}-answered`} value={value} checked={answeredOutcome === value} onChange={() => choose(() => setAnsweredOutcome(value))} />
                    <span>{t(`answeredOutcomes.${value}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="g-field">
              <label className="g-field__label" htmlFor={`${ids}-minutes`}>
                {t("manualCall.minutes")}
              </label>
              <input
                id={`${ids}-minutes`}
                className="g-field__input"
                type="number"
                inputMode="numeric"
                min={0}
                max={MANUAL_CALL_MAX_MINUTES}
                step={1}
                value={minutes}
                onChange={(event) => choose(() => setMinutes(event.target.value))}
              />
            </div>
          </>
        ) : null}
        {lead.phones.length > 0 ? (
          <div className="g-field">
            <label className="g-field__label" htmlFor={`${ids}-phone`}>
              {t("manualCall.phone")}
            </label>
            <select id={`${ids}-phone`} className="g-select" value={phoneId} onChange={(event) => setPhoneId(event.target.value)}>
              {lead.phones.map((phone) => (
                <option key={phone.id} value={phone.id}>
                  {phoneOptionText(t, card, phone)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="g-field">
          <label className="g-field__label" htmlFor={`${ids}-note`}>
            {t("callDesk.notesLabel")}
          </label>
          <textarea
            id={`${ids}-note`}
            className="g-field__input g-desk__notes"
            placeholder={t("callDesk.notesPlaceholder")}
            maxLength={4000}
            value={note}
            onChange={(event) => choose(() => setNote(event.target.value))}
          />
        </div>
        {error ? (
          <Reason tone="warning" role="alert">
            {error}
          </Reason>
        ) : null}
      </form>
    </Dialog>
  );
}
